import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, BookOpenIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { UserGuideProfileSection } from "../../components/dashboard/userGuide/UserGuideProfileSection";

const Section = ({ id, title, children }: { id: string; title: string; children: ReactNode }) => (
  <section id={id} className="scroll-mt-24 border-b border-slate-100 py-8 last:border-b-0">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
  </section>
);

export const UserGuideProfileGettingStarted = () => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    document.title = `${t("user_guide.profile.title")} | Promang`;
  }, [t]);

  useEffect(() => {
    const raw = location.hash?.replace(/^#/, "");
    if (!raw) return;
    const el = document.getElementById(raw);
    if (el) {
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          to="/dashboard/guide"
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t("user_guide.profile.back_to_hub")}
        </Link>

        <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-8 shadow-sm sm:px-8">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
            <UserCircleIcon className="h-4 w-4" />
            {t("user_guide.profile.badge")}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
            {t("user_guide.profile.title")}
          </h1>
          <p className="mt-3 text-sm text-slate-600">{t("user_guide.profile.intro")}</p>

          <div className="mt-6">
            <UserGuideProfileSection />
          </div>

          <Section id="why" title={t("user_guide.profile.section.why.title")}>
            <p>{t("user_guide.profile.section.why.p1")}</p>
            <p>{t("user_guide.profile.section.why.p2")}</p>
          </Section>

          <Section id="where" title={t("user_guide.profile.section.where.title")}>
            <p>
              <BookOpenIcon className="mb-1 inline h-4 w-4 text-slate-500" />{" "}
              {t("user_guide.profile.section.where.p1")}
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
};
